import {
  IonContent,
  IonIcon,
  IonItem as Item,
  IonLabel as Label,
  IonAvatar,
  IonListHeader,
  useIonRouter,
} from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import { search as searchIcon, imageOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { t } from 'ttag';

import Layout, { ButtonType } from '@components/Layout';
import InfiniteList from '@components/List';
import RoutePath from '@custom-types/RoutePath';
import { generateRoute } from '@services/navigation-service';
import { getCompanyInfo, setCompanyInfo } from '@state/company';
import { getEventInfo } from '@state/event';

import { useCompanies } from './queries';
import { Company } from './service';

const NotFound = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px;
`;
const Icon = styled(IonIcon)`
  width: 128px;
  height: 128px;
`;

const Avatar = styled(IonAvatar)`
  --border-radius: unset;
`;
const ImageIcon = styled(IonIcon)`
  width: 40px;
  height: 40px;
`;
const IonItem = styled(Item)`
  margin: 10px 0;
`;
const IonLabel = styled(Label)`
  padding: 5px 0;
`;

const selector = createSelector(
  getEventInfo,
  getCompanyInfo,
  ({ id: eventId, name: eventName }, { id: companyId }) => ({
    eventId,
    eventName,
    companyId,
  })
);

const CompanyItem = ({ company, onClick }: { company: Company; onClick: () => void }) => (
  <IonItem
    // skipcq: JS-0417
    onClick={onClick}
  >
    <Avatar slot="start">
      {company.logoImage?.thumbnailUrl ? (
        <img src={company.logoImage?.thumbnailUrl} alt={t`Company Icon`} />
      ) : (
        <ImageIcon icon={imageOutline} />
      )}
    </Avatar>
    <IonLabel>
      <h2>{company.name}</h2>
    </IonLabel>
  </IonItem>
);

const List = () => {
  const [search, setSearch] = useState('');
  const { eventId, eventName } = useSelector(selector);
  const dispatch = useDispatch();
  const { push: pushRoute } = useIonRouter();

  const {
    fetchNextPage,
    isFetching,
    hasNextPage = false,
    isLoading,
    data: companyPages,
  } = useCompanies(eventId, search);

  const [company] = companyPages?.pages[0]?.data || [];
  const totalCompanyCount = companyPages?.pages[0]?.meta?.pagination?.totalItemsCount ?? 0;
  const shouldRenderList = totalCompanyCount > 1 || search;

  const onSelectCompany = useCallback(
    (selectedCompany: Company) => {
      dispatch(setCompanyInfo(selectedCompany));
      pushRoute(generateRoute(RoutePath.LeadHome));
    },
    [dispatch, pushRoute]
  );

  useEffect(() => {
    if (!shouldRenderList && company) {
      onSelectCompany(company);
    }
  }, [company, onSelectCompany, shouldRenderList]);

  if (shouldRenderList) {
    return (
      <Layout title={eventName} buttonType={ButtonType.Back}>
        <IonContent>
          <InfiniteList
            loadData={fetchNextPage}
            endOfList={!hasNextPage}
            onSearch={setSearch}
            isLoading={isFetching && isLoading}
          >
            <IonListHeader>{t`Company Access`}</IonListHeader>
            {companyPages?.pages?.map(({ data: companies }) =>
              companies.map(companyDetail => (
                <CompanyItem
                  key={companyDetail.id}
                  company={companyDetail}
                  // skipcq: JS-0417
                  onClick={() => onSelectCompany(companyDetail)}
                />
              ))
            )}
            {totalCompanyCount === 0 && (
              <NotFound>
                <Icon size="large" icon={searchIcon} color="warning" />
                <h2>{t`No Results Found`}</h2>
              </NotFound>
            )}
          </InfiniteList>
        </IonContent>
      </Layout>
    );
  }

  return null;
};

export default List;
